import { ObjectRef } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/common/common_pb';
import { PageSpec } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/portal_pb';
import {
  APIProductDetails,
  APIProductListResponse,
  APIProductPlanListSummary,
  APIProductVersionDetails,
  APIProductVersionDetailsRequest,
  DynamicPage,
  EnvironmentPlans
} from 'proto/web_app_grpc_pb';
import { WebAppApi } from 'proto/web_app_grpc_pb_service';
import useSWR, { ConfigInterface } from 'swr';
import { webAppAPI } from './web-app';

// TODO: set up appropriate refresh intervals
const REFRESH_INTERVAL_DEFAULT = 3000;
const REFRESH_INTERVAL_SLOW = 10000;

// TODO: define all keys here to avoid duplicates and for use elsewhere in code base
// Keys
export const API_CATALOG_INFO_KEY = `${WebAppApi.ListAPIProducts.methodName}`;
export const API_PRODUCT_DETAILS_KEY = (ref: ObjectRef.AsObject) =>
  !!ref.name && !!ref.namespace ? `${WebAppApi.GetAPIProductDetails.methodName}/${ref.name}/${ref.namespace}` : null;

export const CURRENT_USER_KEY = `${WebAppApi.GetCurrentUser.methodName}`;
export const PORTAL_SUMMARY_KEY = `${WebAppApi.GetPortalSummary.methodName}`;
export const CUSTOM_STYLES_KEY = `${WebAppApi.GetCustomStyling.methodName}`;

export const GLOBAL_CONFIG: ConfigInterface = {
  shouldRetryOnError: false,
  dedupingInterval: 2000,
  refreshInterval: REFRESH_INTERVAL_DEFAULT
};

export function useListAPIProducts() {
  const { data, error, mutate } = useSWR<APIProductListResponse.AsObject>(
    API_CATALOG_INFO_KEY,
    webAppAPI.listAPIProducts,
    GLOBAL_CONFIG
  );

  return {
    apiCatalogInfo: data,
    apiCatalogInfoError: error,
    apiCatalogInfoLoading: !data && !error,
    apiCatalogInfoMutate: mutate
  };
}

export function useGetAPIProductDetails(apiProductRef: ObjectRef.AsObject) {
  const { data, error, mutate } = useSWR<APIProductDetails.AsObject>(
    API_PRODUCT_DETAILS_KEY(apiProductRef),
    () => webAppAPI.getAPIProductDetails(apiProductRef!),
    GLOBAL_CONFIG
  );

  return {
    apiProductDetails: data,
    apiProductDetailsError: error,
    apiProductDetailsLoading: !data && !error,
    apiProductDetailsMutate: mutate
  };
}

export function useGetAPIProductVersionDetails(
  req: APIProductVersionDetailsRequest.AsObject & { currentUser: string }
) {
  let key =
    !!req?.apiProductRef?.namespace &&
    !!req?.apiProductRef?.name &&
    !!req?.environmentRef?.namespace &&
    !!req?.environmentRef?.name
      ? `${WebAppApi.GetAPIProductVersionDetails.methodName}/${req?.environmentRef?.namespace}_${req?.environmentRef?.name}` +
        `/${req?.apiProductRef?.namespace}_${req?.apiProductRef?.name}/${req?.version}/${req?.currentUser}`
      : null;

  const { data, error, mutate } = useSWR<APIProductVersionDetails.AsObject>(
    key,
    () => webAppAPI.getAPIProductVersionDetails(req),
    GLOBAL_CONFIG
  );

  return {
    apiVersionDetails: data,
    apiVersionDetailsError: error,
    apiVersionDetailsLoading: !data && !error,
    apiVersionDetailsMutate: mutate
  };
}

export function useGetDynamicPages() {
  let key = `${WebAppApi.GetDynamicPages.methodName}`;
  const { data, error, mutate } = useSWR<DynamicPage.AsObject[]>(key, webAppAPI.getDynamicPages, {
    ...GLOBAL_CONFIG,
    refreshInterval: 0
  });

  return {
    dynamicPages: data,
    dynamicPagesError: error,
    dynamicPagesLoading: !data && !error,
    dynamicPagesMutate: mutate
  };
}

export function useGetStaticPages() {
  let key = `${WebAppApi.GetStaticPages.methodName}`;
  const { data, error, mutate } = useSWR<PageSpec.AsObject[]>(key, webAppAPI.getStaticPages, {
    ...GLOBAL_CONFIG,
    refreshInterval: 0
  });

  return {
    staticPages: data,
    staticPagesError: error,
    staticPagesLoading: !data && !error,
    staticPagesMutate: mutate
  };
}

export function useGetEnvironmentUsagePlans(ref: ObjectRef.AsObject) {
  let key =
    !!ref.namespace && !!ref.name
      ? `${WebAppApi.ListAPIProductPlanDetails.methodName}/${ref.namespace}/${ref.name}`
      : null;

  const { data, error, mutate } = useSWR<EnvironmentPlans.AsObject[]>(
    key,
    () => webAppAPI.listAPIProductEnvironmentPlans(ref!),
    GLOBAL_CONFIG
  );

  return {
    environmentUsagePlans: data,
    environmentUsagePlansError: error,
    environmentUsagePlansLoading: !data && !error,
    environmentUsagePlansMutate: mutate
  };
}

export function useListAPIProductPlanDetails() {
  let key = `${WebAppApi.ListAPIProductPlans.methodName}`;
  const { data, error, mutate } = useSWR<APIProductPlanListSummary.AsObject[]>(
    key,
    webAppAPI.listAPIProductPlanDetails,
    GLOBAL_CONFIG
  );

  return {
    apiProductPlans: data,
    apiProductPlansError: error,
    apiProductPlansLoading: !data && !error,
    apiProductPlansMutate: mutate
  };
}

export function useGetCustomStyles() {
  const { data, error, mutate } = useSWR(CUSTOM_STYLES_KEY, webAppAPI.getCustomStyles, {
    ...GLOBAL_CONFIG,
    refreshInterval: REFRESH_INTERVAL_SLOW
  });

  return {
    customStyles: data,
    customStylesError: error,
    customStylesLoading: !data && !error,
    customStylesMutate: mutate
  };
}

// TODO: this probably doesn't need to refresh every 3 seconds
export function useGetPortalSummary() {
  const { data, error } = useSWR(PORTAL_SUMMARY_KEY, webAppAPI.getPortalSummary, {
    ...GLOBAL_CONFIG,
    refreshInterval: REFRESH_INTERVAL_SLOW
  });

  return {
    portalSummary: data,
    portalSummaryError: error,
    hasOIDCLoginEnabled: !!data?.oidcLoginUrl,
    portalSummaryLoading: !data && !error
  };
}

// TODO: this probably doesn't need to refresh every 3 seconds
export function useGetCurrentUser() {
  const { data, error } = useSWR(CURRENT_USER_KEY, webAppAPI.getCurrentUser, {
    ...GLOBAL_CONFIG,
    refreshInterval: REFRESH_INTERVAL_SLOW
  });

  return {
    currentUser: data,
    isLoggedIn: data !== '',
    currentUserError: error,
    currentUserLoading: !data && !error
  };
}

export function useGetPageExtensionsMap() {
  let key = `${WebAppApi.serviceName}.${WebAppApi.GetPageExtensions.methodName}`;
  const { data, error, revalidate } = useSWR<[string, string][]>(key, webAppAPI.getPageExtensionsMap, {
    ...GLOBAL_CONFIG,
    refreshInterval: REFRESH_INTERVAL_SLOW
  });

  return {
    pageExtensionsMap: data,
    pageExtensionsMapError: error,
    refreshPageExtensionsMap: revalidate,
    pageExtensionsMapLoading: !data && !error
  };
}
